import * as Icons from "lucide-react";
import { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import type { Tile } from "../../types";

interface Props {
  tile: Tile;
  variant?: "row" | "card";
  deptColor?: string;
}

export default function TileCard({ tile, variant = "row", deptColor }: Props) {
  const Icon =
    ((Icons as unknown) as Record<string, Icons.LucideIcon>)[toPascal(tile.icon)] ??
    Icons.ArrowUpRight;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const isContent = tile.kind === "content";
  const iconColor = deptColor ?? "#5c544a";

  const rowInner = (
    <>
      <div
        className="size-8 rounded-lg grid place-items-center shrink-0 transition-colors"
        style={{
          backgroundColor: `${iconColor}14`,
          color: iconColor,
        }}
      >
        <Icon className="size-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-medium text-ink-900 truncate">{tile.name}</span>
          {tile.badge && <span className="chip-accent">{tile.badge}</span>}
        </div>
        {tile.description && (
          <p className="text-[12.5px] text-ink-500 truncate mt-0.5">{tile.description}</p>
        )}
      </div>
      {isContent ? (
        <Icons.BookOpen className="size-3.5 text-ink-300 group-hover:text-ink-900 transition-colors shrink-0" />
      ) : (
        <Icons.ArrowUpRight className="size-3.5 text-ink-300 group-hover:text-ink-900 transition-colors shrink-0" />
      )}
    </>
  );

  const cardInner = (
    <>
      <div
        className="size-11 rounded-xl grid place-items-center shrink-0"
        style={{
          backgroundColor: `${iconColor}1f`,
          color: iconColor,
        }}
      >
        <Icon className="size-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[14.5px] font-semibold text-ink-900 truncate">{tile.name}</span>
          {tile.badge && <span className="chip-accent">{tile.badge}</span>}
        </div>
        {tile.description && (
          <p className="text-[12.5px] text-ink-500 line-clamp-2 mt-1 leading-[1.5]">
            {tile.description}
          </p>
        )}
      </div>
      {isContent ? (
        <Icons.BookOpen className="size-3.5 text-ink-300 group-hover:text-ink-900 transition-colors shrink-0 mt-1" />
      ) : (
        <Icons.ArrowUpRight className="size-3.5 text-ink-300 group-hover:text-ink-900 transition-colors shrink-0 mt-1" />
      )}
    </>
  );

  const baseRow =
    "group flex items-center gap-3 px-5 py-3.5 hover:bg-cream-50 transition-colors";
  const baseCard =
    "group surface p-5 flex items-start gap-3 hover:shadow-soft hover:-translate-y-0.5 transition-all";

  if (isContent) {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`${variant === "row" ? baseRow : baseCard} text-left w-full`}
        >
          {variant === "row" ? rowInner : cardInner}
        </button>
        {open && <ContentModal tile={tile} onClose={() => setOpen(false)} />}
      </>
    );
  }

  return (
    <a
      href={tile.url ?? "#"}
      target={tile.open_in_new_tab ? "_blank" : undefined}
      rel={tile.open_in_new_tab ? "noopener noreferrer" : undefined}
      className={variant === "row" ? baseRow : baseCard}
    >
      {variant === "row" ? rowInner : cardInner}
    </a>
  );
}

function ContentModal({ tile, onClose }: { tile: Tile; onClose: () => void }) {
  const safeHtml = DOMPurify.sanitize(tile.content_html ?? "", {
    USE_PROFILES: { html: true },
    ALLOWED_ATTR: ["href", "target", "rel", "class", "style"],
  });

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm grid place-items-center p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-2xl max-h-[88vh] flex flex-col overflow-hidden shadow-modal animate-scale-in !rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-ink-100 flex items-start justify-between gap-4 shrink-0">
          <div className="min-w-0">
            <h2 className="display text-[22px] font-medium tracking-tight text-ink-900 leading-tight">
              {tile.name}
            </h2>
            {tile.description && (
              <p className="text-[12.5px] text-ink-500 mt-0.5">{tile.description}</p>
            )}
          </div>
          <button onClick={onClose} className="btn-icon shrink-0" aria-label="close">
            <Icons.X className="size-4" />
          </button>
        </header>
        <div
          className="px-6 py-5 overflow-y-auto prose prose-sm max-w-none prose-headings:font-display prose-headings:italic prose-headings:font-medium prose-headings:tracking-tight prose-a:text-accent prose-strong:text-ink-900 prose-p:text-ink-700 prose-li:text-ink-700"
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
        <footer className="px-6 py-3 border-t border-ink-100 flex items-center justify-end gap-2 text-[11px] text-ink-400 shrink-0 bg-cream-50">
          <span>Press</span><kbd className="kbd">Esc</kbd><span>to close</span>
        </footer>
      </div>
    </div>
  );
}

function toPascal(s: string): string {
  return s
    .split(/[-_\s]/)
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ""))
    .join("");
}
